import { useContext, useMemo } from "react";
import { Badge } from "@mantine/core";
import { IconUserUp } from "@tabler/icons-react";
import { useParams } from "react-router-dom";

import {
  useGetWorkspaceUsers,
  useTransferWorkspaceOwnership,
} from "../../../api/workspace";
import { AuthContext } from "../../../services/context";
import { useConfirm } from "../../../hooks/useConfirm";
import { DataTable } from "../../DataTable";
import { TableAction, TableActions, actionsColumn } from "../../TableActions";
import RemoveUserModalButton from "./RemoveUserModal";
import RoleBadge from "./../../RoleBadges";
import { AccountIdentity, fullName } from "./AccountIdentity";
import { SectionLabel } from "./SectionLabel";

// The badges that matter in this list: who owns the workspace, and which row
// is the person looking at it.
function MemberCell({ member, isOwner, isYou }) {
  return (
    <AccountIdentity
      user={member}
      badges={
        <>
          {isOwner && (
            <Badge color="blue" size="sm">
              Owner
            </Badge>
          )}
          {isYou && (
            <Badge color="gray" variant="outline" size="sm">
              You
            </Badge>
          )}
        </>
      }
    />
  );
}

export const WorkspaceMembersTable = ({
  workspace,
  canManageMembers,
  isSysAdmin,
}) => {
  const { id } = useParams();
  const { user: currentUser } = useContext(AuthContext);
  const { data: workspaceUsers, isPending } = useGetWorkspaceUsers(id);
  const { mutate: transferOwnership } = useTransferWorkspaceOwnership({
    workspaceId: id,
  });
  const confirm = useConfirm();

  const ownerId = workspace?.ownerId;

  // Owner first, then alphabetical: the list is short, and the one row that
  // cannot be removed is the one worth finding without reading.
  const members = useMemo(
    () =>
      [...(workspaceUsers ?? [])].sort((a, b) => {
        if (a.id === ownerId) return -1;
        if (b.id === ownerId) return 1;
        return fullName(a).localeCompare(fullName(b));
      }),
    [workspaceUsers, ownerId]
  );

  const confirmTransfer = (row) =>
    confirm({
      title: "Transfer workspace ownership",
      message: `Make ${fullName(row)} the owner of this workspace? The current owner becomes a regular member.`,
      confirmLabel: "Transfer ownership",
      onConfirm: () => transferOwnership({ ownerId: row.id }),
    });

  const columns = [
    {
      accessor: "name",
      title: "Member",
      render: (row) => (
        <MemberCell
          member={row}
          isOwner={row.id === ownerId}
          isYou={row.id === currentUser?.id}
        />
      ),
    },
    {
      accessor: "role",
      // Keeps the badge from being squeezed when the table is narrow: the
      // table scrolls instead.
      noWrap: true,
      render: (row) => <RoleBadge roleName={row.role} />,
    },
  ];

  if (canManageMembers) {
    columns.push(
      // The owner cannot be removed; ownership must be transferred first.
      // Only sysAdmins may reassign ownership, and only to another member.
      actionsColumn((row) =>
        row.id === ownerId ? null : (
          <TableActions>
            {isSysAdmin && (
              <TableAction
                label="Make workspace owner"
                icon={IconUserUp}
                color="blue"
                onClick={() => confirmTransfer(row)}
              />
            )}
            <RemoveUserModalButton user={row} workspaceName={workspace?.name} />
          </TableActions>
        )
      )
    );
  }

  return (
    <>
      <SectionLabel title="Members" count={workspaceUsers?.length} />
      <DataTable
        records={members}
        columns={columns}
        fetching={isPending}
        withColumnBorders={false}
        minHeight={120}
        verticalSpacing="sm"
      />
    </>
  );
};
